import React, { useEffect, useState } from "react";
import { Dialog } from "primereact/dialog";
import { ProgressSpinner } from "primereact/progressspinner";
import mammoth from "mammoth";

type DocumentPreviewModalProps = {
  visible: boolean;
  onHide: () => void;
  filename: string;
  fetchBlob: () => Promise<Blob>;
};

type PreviewState =
  | { status: "idle" }
  | { status: "loading" }
  | { status: "error"; message: string }
  | { status: "pdf"; objectUrl: string }
  | { status: "docx"; html: string };

export const canPreview = (filename: string): boolean => {
  const lower = filename.toLowerCase();
  return lower.endsWith(".pdf") || lower.endsWith(".docx");
};

export const DocumentPreviewModal: React.FC<DocumentPreviewModalProps> = ({
  visible,
  onHide,
  filename,
  fetchBlob,
}) => {
  const [preview, setPreview] = useState<PreviewState>({ status: "idle" });

  const isPdf = filename.toLowerCase().endsWith(".pdf");
  const isDocx = filename.toLowerCase().endsWith(".docx");

  useEffect(() => {
    if (!visible) {
      setPreview((prev) => {
        if (prev.status === "pdf") {
          URL.revokeObjectURL(prev.objectUrl);
        }
        return { status: "idle" };
      });
      return;
    }

    let cancelled = false;

    const load = async () => {
      setPreview({ status: "loading" });
      try {
        const blob = await fetchBlob();
        if (cancelled) return;

        if (isPdf) {
          const pdfBlob =
            blob.type === "application/pdf"
              ? blob
              : new Blob([blob], { type: "application/pdf" });
          const objectUrl = URL.createObjectURL(pdfBlob);
          if (cancelled) {
            URL.revokeObjectURL(objectUrl);
            return;
          }
          setPreview({ status: "pdf", objectUrl });
        } else if (isDocx) {
          const arrayBuffer = await blob.arrayBuffer();
          if (cancelled) return;
          const result = await mammoth.convertToHtml({ arrayBuffer });
          if (cancelled) return;
          setPreview({ status: "docx", html: result.value });
        } else {
          setPreview({
            status: "error",
            message: "Unsupported file type for preview.",
          });
        }
      } catch (err) {
        if (cancelled) return;
        console.error("Document preview error:", err);
        setPreview({
          status: "error",
          message:
            err instanceof Error
              ? err.message
              : "Failed to load document preview.",
        });
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [visible, fetchBlob, isPdf, isDocx]);

  // Cleanup object URL on unmount
  useEffect(() => {
    return () => {
      if (preview.status === "pdf") {
        URL.revokeObjectURL(preview.objectUrl);
      }
    };
  }, []);

  return (
    <Dialog
      visible={visible}
      onHide={onHide}
      header={filename}
      draggable={false}
      maximizable
      style={{ width: "60vw" }}
      contentStyle={{
        overflow: "auto",
        minHeight: "60vh",
        maxHeight: "80vh",
        padding: 0,
      }}
    >
      {preview.status === "loading" && (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            height: "60vh",
            gap: "12px",
          }}
        >
          <ProgressSpinner
            style={{ width: "50px", height: "50px" }}
            strokeWidth="4"
          />
          <span>Loading preview...</span>
        </div>
      )}

      {preview.status === "error" && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            height: "60vh",
            color: "var(--red-500, #ef4444)",
            padding: "20px",
          }}
        >
          <i
            className="pi pi-exclamation-triangle"
            style={{ marginRight: "8px" }}
          />
          {preview.message}
        </div>
      )}

      {preview.status === "pdf" && (
        <iframe
          src={preview.objectUrl}
          title={`Preview of ${filename}`}
          style={{
            width: "100%",
            height: "80vh",
            border: "none",
          }}
        />
      )}

      {preview.status === "docx" && (
        <div
          style={{
            width: "100%",
            padding: "24px",
            lineHeight: 1.6,
          }}
          dangerouslySetInnerHTML={{ __html: preview.html }}
        />
      )}
    </Dialog>
  );
};
